import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import '../styles/Profile.css';
import User from '../models/User';
import formatStartingTime from '../utils/formatStartingTime';
import { resetUserState, setUser } from '../redux/users/reducer';
import { logoutAsync } from '../redux/users/thunks';
import formatMonthFromSlotId from '../utils/formatMonthFromSlotId';
import dayjs from 'dayjs';
import moment from 'moment/moment';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

export default function Profile() {
  const [currentUser, setCurrentUser] = useState(null);
  const [fetchUserError, setFetchUserError] = useState(false);
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const user = useSelector((state) => state.users.user);

  const token = localStorage.getItem('token');

  const isLoggedIn =
    useSelector((state) => state.users.isLoggedIn) &&
    localStorage.getItem('token') !== null;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const changePassword = async () => {
    try {
      const updatedUser = {
        ...user,
        password: document.getElementById('new-password-field').value,
      };

      const response = await fetch(
        'https://api.innofitness.ca/users/change-password',
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            access: localStorage.getItem('token'),
          },
          body: JSON.stringify(updatedUser),
        }
      );

      if (response.ok) {
        window.alert('Password changed successfully!');
        window.location.reload();
      }
    } catch (error) {
      if (error.message.includes('access token expired')) {
        localStorage.removeItem('token');
        dispatch(resetUserState());
        navigate('/');
      } else {
        window.alert('Error changing password. Please try again.');
      }
    }
  };

  const logout = () => {
    localStorage.setItem('bookings', null);
    dispatch(logoutAsync(token));
    dispatch(resetUserState());
    navigate('/');
  };

  const isSlotPast = (slotDate) => {
    return moment(slotDate).isBefore(moment(), 'minute');
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(
          'https://api.innofitness.ca/users/profile',
          {
            method: 'GET',
            headers: {
              access: localStorage.getItem('token'),
            },
          }
        );

        if (response.ok) {
          const userData = await response.json();
          const authenticatedUser = new User(
            userData.id,
            userData.username,
            null,
            userData.pin,
            userData.firstName,
            userData.lastName,
            userData.waiverSigned,
            userData.role,
            userData.createdAt,
            userData.slots,
            userData.jokguTeam
          );
          setCurrentUser(authenticatedUser);
          localStorage.setItem('bookings', authenticatedUser.slots);
          dispatch(setUser(authenticatedUser));
        } else {
          setFetchUserError(true);
        }
      } catch (error) {
        if (error.message.includes('access token expired')) {
          localStorage.removeItem('token');
          dispatch(resetUserState());
          navigate('/');
        } else {
          window.alert('Error fetching user data. Please try again.');
        }
      }
    };

    fetchUser();
  }, []);

  const handleDelete = async (id) => {
    try {
      const response = await fetch('https://api.innofitness.ca/bookings', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          access: localStorage.getItem('token'),
        },
        body: id,
      });

      if (response.ok) {
        window.location.reload();
      }
    } catch (error) {
      if (error.message.includes('access token expired')) {
        localStorage.removeItem('token');
        dispatch(resetUserState());
        navigate('/');
      } else {
        window.alert('Error deleting booking. Please try again.');
      }
    }
  };

  if (currentUser === null && fetchUserError) {
    navigate('/');
  } else if (currentUser === null) {
    return (
      <Container
        className="page-container"
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress color="inherit" />
      </Container>
    );
  } else if (!user || !isLoggedIn) {
    navigate('/');
  }

  return (
    <Container
      className="page-container"
      sx={{ maxWidth: '1200px', px: 'max(2%, 16px) !important' }}
    >
      <div className="page-title">
        <h1 style={{ textAlign: 'center' }}>
          Welcome, {currentUser?.firstName} {currentUser?.lastName}!
        </h1>
      </div>
      <p>
        <b>Email:</b> {currentUser?.username}
      </p>
      <p>
        <b>Password:</b>{' '}
        <Button
          variant="outlined"
          color="error"
          onClick={handleOpen}
          sx={{
            '&.MuiButton-root': {
              textTransform: 'none',
              borderRadius: '0px',
              padding: '2px 5px',
            },
          }}
        >
          Change Password
        </Button>
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="change-password-modal"
          // aria-describedby="change-password-modal"
        >
          <Box sx={modalStyle}>
            <div className="change-password-modal">
              <Typography id="change-password-modal-title" variant="h5">
                Change Password
              </Typography>
              <div className="new-password">
                New Password:{' '}
                <TextField
                  id="new-password-field"
                  variant="outlined"
                  type="password"
                ></TextField>
              </div>
              <div className="change-password-buttons">
                <Button
                  variant="contained"
                  color="error"
                  onClick={changePassword}
                  sx={{ width: '100px' }}
                >
                  Submit
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  sx={{ width: '100px' }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Box>
        </Modal>
      </p>
      <p>
        <b>PIN:</b> {currentUser?.pin.toString().padStart(4, '0')}
      </p>
      <p>
        <b>Member Since:</b>{' '}
        {dayjs(currentUser?.createdAt).format('MMMM D, YYYY')}
      </p>
      <p>
        <b>Gym Bookings:</b>
      </p>
      {currentUser?.slots.map((slot, index) => {
        if (slot === null || slot === undefined) {
          return;
        }
        const bookingSchedule =
          formatMonthFromSlotId(slot.id.slice(2, 4)) +
          ' ' +
          slot.id.slice(4, 6) +
          ', 20' +
          slot.id.slice(0, 2);
        const dateFormatted = dayjs(slot.date).format('MMMM D, YYYY');
        const bookedTimeFormatted = dayjs(slot.bookedDate).format('h:mmA');
        const startingTimeFormatted = formatStartingTime(slot.startingTime);

        return (
          <div
            key={slot.id}
            style={{
              marginLeft: '24px',
              display: 'flex',
              gap: '10px',
            }}
          >
            <p>
              <b>Booking {index + 1}:</b>{' '}
              <span className={isSlotPast(slot.date) ? 'past-booking' : ''}>
                Scheduled for {bookingSchedule} at {startingTimeFormatted}{' '}
                (Booked on {dateFormatted} at {bookedTimeFormatted})
              </span>
            </p>
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleDelete(slot.id)}
              disabled={isSlotPast(slot.date)}
              sx={{
                padding: '0',
                width: '24px',
                minWidth: '24px',
                height: '24px',
                '&.MuiButton-root': {
                  flexWrap: 'wrap',
                  alignSelf: 'center',
                },
              }}
            >
              X
            </Button>
          </div>
        );
      })}
      <p style={{ marginBottom: !currentUser?.waiverSigned ? 0 : '16px' }}>
        <b>Waiver Signed:</b>{' '}
        <span
          style={{
            color: currentUser?.waiverSigned ? 'black' : '#e65100',
            fontWeight: currentUser?.waiverSigned ? 'normal' : 'bold',
          }}
        >
          {currentUser?.waiverSigned ? 'Yes :)' : 'No :('}
        </span>
      </p>
      {!currentUser?.waiverSigned && (
        <p
          style={{
            color: '#e65100',
            fontSize: '90%',
            marginTop: 0,
          }}
        >
          Please click "AGREEMENT" from the menu bar and agree to our terms to
          get started!
        </p>
      )}
      {currentUser?.jokguTeam && (
        <p>
          <b>Jokgu Team:</b> {currentUser?.jokguTeam} 🏐
        </p>
      )}
      <Button
        variant="outlined"
        color="error"
        onClick={() => logout()}
        sx={{ width: '150px', margin: '40px auto' }}
      >
        LogOut
      </Button>
    </Container>
  );
}
